'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'

export default function RegisterPage() {
  const [step, setStep] = useState<'register' | 'verify'>('register')
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    phone: ''
  })
  const [otpData, setOtpData] = useState({
    userId: '',
    otpCode: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const router = useRouter()

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Şifrələr uyğun gəlmir')
      setLoading(false)
      return
    }

    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
          phone: formData.phone
        }),
      })

      const data = await response.json()

      if (data.success) {
        setOtpData({ ...otpData, userId: data.userId })
        setSuccess(data.message)
        setStep('verify')
      } else {
        setError(data.message || 'Qeydiyyat uğursuz oldu')
      }
    } catch {
      setError('Şəbəkə xətası baş verdi')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')

    try {
      const response = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: otpData.userId,
          otpCode: otpData.otpCode
        }),
      })

      const data = await response.json()

      if (data.success) {
        setSuccess(data.message)
        // Redirect to login page after successful verification
        setTimeout(() => {
          router.push('/customer/login?registered=true')
        }, 2000)
      } else {
        setError(data.message || 'Təsdiq uğursuz oldu')
      }
    } catch {
      setError('Şəbəkə xətası baş verdi')
    } finally {
      setLoading(false)
    }
  }

  const handleResendOTP = async () => {
    setLoading(true)
    setError('')
    setSuccess('')

    try {
      const response = await fetch('/api/auth/resend-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: otpData.userId
        }),
      })

      const data = await response.json()

      if (data.success) {
        setSuccess(data.message)
      } else {
        setError(data.message || 'OTP yenidən göndərilə bilmədi')
      }
    } catch {
      setError('Şəbəkə xətası baş verdi')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css" rel="stylesheet" />
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-600 to-blue-600 flex items-center justify-center p-5">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <i className="fas fa-id-card text-5xl text-purple-600 mb-4"></i>
            <h1 className="text-2xl font-semibold text-black mb-2">
              {step === 'register' ? 'Qeydiyyat' : 'Telefon Təsdiqi'}
            </h1>
            <p className="text-gray-600">
              {step === 'register' 
                ? 'Cardify NFC kartınız üçün hesab yaradın' 
                : 'Telefon nömrənizə göndərilən kodu daxil edin'}
            </p>
          </div>

          {/* Success Message */}
          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg flex items-center gap-2">
              <i className="fas fa-check-circle"></i>
              {success}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center gap-2">
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
          )}

          {step === 'register' ? (
            <form onSubmit={handleRegister}>
              <div className="mb-4">
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                  İstifadəçi adı
                </label>
                <input
                  type="text"
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="İstifadəçi adı"
                  required
                  minLength={3}
                  maxLength={30}
                  pattern="[a-zA-Z0-9_]+"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">3-30 simvol, yalnız hərf, rəqəm və _</p>
              </div>

              <div className="mb-4">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Telefon nömrəsi
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+994501234567"
                  required
                  pattern="\+994[0-9]{9}"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">+994XXXXXXXXX formatında</p>
              </div>

              <div className="mb-4">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Şifrə
                </label>
                <input
                  type="password"
                  id="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Güclü şifrə daxil edin"
                  required
                  minLength={8}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Ən azı 8 simvol, hərf və rəqəm</p>
              </div>

              <div className="mb-6">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Şifrə təkrarı
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="Şifrəni təkrar edin"
                  required
                  minLength={8}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <button 
                type="submit" 
                disabled={loading}
                className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Qeydiyyat edilir...
                  </>
                ) : (
                  <>
                    <i className="fas fa-user-plus"></i>
                    Qeydiyyatdan keç
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP}>
              <div className="mb-6">
                <label htmlFor="otpCode" className="block text-sm font-medium text-gray-700 mb-2">
                  Təsdiq kodu
                </label>
                <input
                  type="text"
                  id="otpCode"
                  value={otpData.otpCode}
                  onChange={(e) => setOtpData({ ...otpData, otpCode: e.target.value })}
                  placeholder="6 rəqəmli kod"
                  required
                  minLength={6}
                  maxLength={6}
                  pattern="[0-9]{6}"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center text-2xl tracking-widest"
                />
                <p className="text-xs text-gray-500 mt-1 text-center">SMS ilə göndərilən 6 rəqəmli kodu daxil edin</p>
              </div>

              <button 
                type="submit" 
                disabled={loading}
                className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Təsdiq edilir...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check"></i>
                    Təsdiq et
                  </>
                )}
              </button>

              <button 
                type="button"
                onClick={handleResendOTP}
                disabled={loading}
                className="w-full bg-gray-500 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="fas fa-redo"></i>
                Yenidən göndər
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-gray-600 text-sm">
              Artıq hesabınız var?{' '}
              <Link href="/customer/login" className="text-purple-600 hover:text-purple-800 font-medium">
                Giriş edin
              </Link>
            </p>
          </div>
        </div>
      </div>
    </>
  )
}